import { advancedGameIndex, readOnlyGameIndex } from './game-details';
import type { Availability, SearchEntry } from './types';

// 行き先と別名はここに集約する。画面側の data-search-id と対応する。
type Item = [id: string, name: string, keywords: string, availability?: Availability, permission?: 'operate'];
function section(tab: string, sectionId: string, description: string, items: Item[]): SearchEntry[] {
  return items.map(([id, name, keywords, availability, permission]) => ({
    id,
    name,
    keywords,
    availability,
    permission,
    description,
    path: '/servers/:id',
    tab,
    section: sectionId || undefined,
    focus: id,
  }));
}

const entries: SearchEntry[] = [
  ...section('overview', '', '概要', [
    ['overview', 'サーバーの状態・性能', 'TPS MSPT CPU メモリ グラフ 接続先 アドレス 起動 停止 再起動'],
  ]),
  ...section('console', '', 'コンソール', [['console', 'コンソール・ログ', 'console log コマンド エラー say']]),
  ...section('players', '', 'プレイヤー', [
    ['players', 'オンラインのプレイヤー', 'online 接続 人数 一覧'],
    ['player-kick', 'プレイヤーのキック', 'kick 追い出す 切断', 'all', 'operate'],
    ['player-ban', 'プレイヤーの BAN・解除', 'ban unban pardon 出禁 参加禁止 IP ban-ip', 'all', 'operate'],
    ['player-op', 'プレイヤーの OP・解除', 'op deop 管理者 権限 オペレーター', 'all', 'operate'],
    ['player-whitelist', 'ホワイトリストの参加者', 'whitelist 許可リスト 追加 削除 有効 無効'],
  ]),
  ...section('files', '', 'ファイル', [
    ['files', 'ファイルの編集・アップロード', 'file config 設定ファイル jar データパック ダウンロード フォルダー'],
    ['sftp-connection', 'SFTP 接続先', 'sftp SSH ファイル転送 ホスト ポート'],
  ]),
  ...section('extensions', 'mods', 'Mod・プラグイン', [
    ['mods', 'Mod の追加・削除', 'mod modpack プラグイン Modrinth CurseForge jar 依存 アップロード', 'mods'],
    ['plugins', 'プラグインの追加・削除', 'plugin mod modpack Modrinth CurseForge jar 依存 アップロード', 'plugins'],
    ['mod-side', 'Mod のクライアント区分', 'client 必須 任意 不要 自動 プレイヤー PC 配布', 'mods'],
    ['mod-verify', 'クライアント Mod の起動テスト', '検証 再テスト エラー ログ ランチャー', 'mods'],
    ['modpack', 'Modpack の導入・更新', 'modpack モッドパック Modrinth CurseForge 一括 入れ替え', 'mods'],
  ]),
  ...section('extensions', 'bedrock', '統合版', [
    ['bedrock', '統合版の参加', 'bedrock Geyser Floodgate スマホ Switch クロスプレイ 有効 無効'],
    ['bedrock-port', '統合版の UDP ポート', 'bedrock port 19132 接続'],
  ]),
  ...section('extensions', 'ports', 'ポート', [
    ['ports', '追加ポートの公開・削除', 'port TCP UDP ネットワーク 外部 接続'],
    ['port-internal', '内部ポート・プロトコル', 'port TCP UDP 名前 追加 変更'],
    ['port-name', '追加ポートの名前', 'port ラベル 識別'],
    ['port-protocol', '追加ポートのプロトコル', 'port TCP UDP 通信'],
  ]),
  ...section('extensions', 'launcher', 'ランチャー', [
    ['launcher-publish', 'ランチャーに公開する', 'launcher 配布 パック Prism mrpack zip 一覧'],
    [
      'launcher-gate',
      '入れるクライアントの制限（ランチャー経由だけ）',
      'launcher 制限 クライアント 改造 チート 荒らし 統合版',
    ],
    [
      'client-template',
      'クライアント Mod テンプレートの選択',
      'client modpack ランチャー Fabric 割り当て',
      'client-template',
    ],
    ['client-verify', 'クライアント Mod の起動テスト', '検証 再テスト エラー ログ ランチャー', 'client-template'],
    [
      'admin-client-template',
      '管理者用のクライアント Mod テンプレート',
      'admin 運営 管理者 ランチャー WorldEdit 構成',
      'client-template',
    ],
    ['launcher-policy', 'ランチャーの構成チェック', 'policy 不一致 警告 拒否 構成が違う', 'mods'],
    ['launcher-extra', '追加 Mod の許可', '許可する Mod 名前 パターン allow extra', 'mods'],
    ['launcher-memory', 'ランチャーの推奨メモリ', 'RAM メモリ 割り当て GB 推奨 memory', 'mods'],
  ]),
  ...section('backups', '', 'バックアップ', [
    ['snapshots', 'スナップショットの作成', 'snapshot バックアップ backup 保存 メモ'],
    ['snapshot-restore', 'スナップショットの復元・削除', 'snapshot restore 戻す バックアップ', 'all', 'operate'],
  ]),
  ...section('settings', 'game-settings', 'ゲーム設定', [
    ['game-gamemode', 'デフォルトのゲームモード', 'gamemode サバイバル クリエイティブ アドベンチャー スペクテイター'],
    ['game-difficulty', '難易度', 'difficulty ピースフル イージー ノーマル ハード'],
    ['game-hardcore', 'ハードコア', 'hardcore 死亡 復活'],
    ['game-pvp', 'PvP', 'pvp 対人 プレイヤー 攻撃 ダメージ'],
    ['game-allow-flight', '飛行を許可', 'allow-flight flight フライト 空を飛ぶ キック'],
    ['game-allow-nether', 'ネザーを使う', 'allow-nether nether ディメンション 有効 無効'],
    ['game-allow-end', 'エンドを使う', 'allow-end end ディメンション 有効 無効', 'plugins'],
    ['game-max-players', '最大人数（最大プレイヤー数）', 'max-players 人数 上限 定員 スロット'],
    ['game-spawn-protection', 'スポーン保護範囲', 'spawn-protection 初期地点 ブロック 保護'],
    ['game-motd', 'サーバーの説明（MOTD）', 'motd サーバーリスト メッセージ 名前'],
  ]),
  ...advancedGameIndex,
  ...readOnlyGameIndex,
  ...section('settings', 'software', 'ソフトとバージョン', [
    [
      'software',
      'ソフト・Minecraft の版を変更',
      'version バージョン アップデート ダウングレード Paper Fabric Forge NeoForge Purpur vanilla ビルド ローダー',
    ],
  ]),
  ...section('settings', 'resources', 'リソース', [
    ['res-memory', 'メモリ', 'RAM VM memory リソース 容量'],
    ['heap', 'ヒープの最小・最大メモリ', 'RAM Xmx Xms Java heap JVM リソース'],
    ['res-cores', 'CPU コア数', 'cpu processor プロセッサ リソース'],
    ['res-disk', 'データディスクの拡張', 'disk storage 容量 ストレージ 空き 使用量 リソース'],
    ['move-node', 'ノードを移す', 'node migrate 移動 引っ越し VM', 'all', 'operate'],
    ['world-ip', 'ワールド VM の固定 IP', 'IP address DHCP ネットワーク アドレス 変更 移行'],
    ['jvm-args', 'Java の起動引数', 'JVM arguments オプション GC 追加の引数 起動コマンド'],
    ['jvm-flags', "Aikar's flags", 'Java JVM GC 最適化 フラグ 有効 無効'],
  ]),
  ...section('settings', 'schedule', 'スケジュール', [
    ['schedule', 'スケジュールの追加・編集', 'schedule タスク 定期 自動 時刻 曜日 有効 無効 削除'],
    ['schedule-restart', '定期的な再起動', 'restart 自動 予約 時刻 曜日 予告 接続中 スキップ'],
    ['schedule-command', '定期的なコマンド実行', 'command 自動 say お知らせ 予約 時刻 曜日'],
    ['schedule-warn', '再起動の予告', '何分前 スケジュール 告知 ゲーム内 お知らせ'],
    ['schedule-skip', 'プレイヤーがいたら再起動しない', '接続中 スケジュール skip online スキップ'],
  ]),
  ...section('settings', 'danger', '危険な操作 › ワールドの生成', [
    [
      'world-type',
      'ワールドの生成方法',
      'world generation 地形 normal flat スーパーフラット ボイド void 何もない 大きなバイオーム amplified アンプリファイド',
    ],
    ['world-seed', 'ワールドのシード値', 'seed level-seed 乱数 種'],
    ['world-structures', '構造物を生成', 'generate-structures 村 遺跡'],
    ['world-custom', 'Mod・データパックの生成方法 ID', 'namespace custom generator-settings カスタム 地形'],
    ['world-biome', 'バイオーム ID', 'biome single_biome シングルバイオーム'],
    ['flat-preset', 'フラットのプリセット・層', 'flat ブロック 厚さ 順序 追加 削除'],
    ['flat-structures', 'フラット内の構造物', 'flat structures 村 遺跡'],
    ['flat-features', 'フラットの地形の装飾', 'flat features 草木'],
    ['flat-lakes', 'フラットの湖', 'flat lakes 水'],
    ['void-platform', 'ボイドのスポーン地点の足場', 'void 石 足場 何もない 落下'],
    [
      'regenerate',
      'ワールドを再生成',
      'regenerate リセット 初期化 作り直す プレイヤーデータ 統計 進捗 シード',
      'all',
      'operate',
    ],
    ['delete-server', 'サーバーを削除', 'delete 消す VM データディスク', 'all', 'operate'],
  ]),
];

const focusAliases: Record<string, string> = {
  'player-kick': 'players',
  'snapshot-restore': 'snapshots',
  'schedule-restart': 'schedule',
  'schedule-command': 'schedule',
  'schedule-warn': 'schedule',
  'schedule-skip': 'schedule',
};
export const serverIndex = entries.map((entry) => ({ ...entry, focus: focusAliases[entry.id] ?? entry.focus }));
